import type { UsersTranslations } from "@/shared/lang/interfaces/users";

import { UserStatus } from "../types/create-user.types";

export type StatusVariant = "success" | "warning" | "danger" | "neutral";

export interface StatusPresentation {
  variant: StatusVariant;
  labelKey: keyof UsersTranslations;
}

/**
 * How an account status is drawn, in one place so the table row and the detail drawer cannot disagree — they
 * did: the drawer used to print the raw enum (`pending_verification`) with a hardcoded neutral pill while the
 * row two clicks away showed "Sin verificar" in danger.
 *
 * `inactive` is deliberately `neutral`: a deliberate end state is not an error. Only a suspension earns
 * `danger`.
 */
export const STATUS_PRESENTATION: Record<UserStatus, StatusPresentation> = {
  [UserStatus.ACTIVE]: { variant: "success", labelKey: "status_active" },
  [UserStatus.INACTIVE]: { variant: "neutral", labelKey: "status_inactive" },
  [UserStatus.SUSPENDED]: { variant: "danger", labelKey: "status_suspended" },
  [UserStatus.PENDING_VERIFICATION]: { variant: "warning", labelKey: "status_pending_verification" },
};

/**
 * Resolves a status to its pill. A value the enum does not know — one added by a later migration — falls back
 * to the raw string rather than being mislabelled as something it is not.
 */
export function resolveStatusPresentation(
  status: UserStatus,
  translate: (key: keyof UsersTranslations) => string,
): { variant: StatusVariant; label: string } {
  const presentation = STATUS_PRESENTATION[status];
  if (!presentation) return { variant: "neutral", label: String(status) };
  return { variant: presentation.variant, label: translate(presentation.labelKey) };
}
